/**
 * Os avisos temporários: como mostrar o prazo e o resumo no ícone.
 *
 * Pedido da Gisele (19/10/2026). O ícone ao lado do sino mostra os avisos que
 * ainda valem; quem decide o que ainda vale é o servidor, não este módulo --
 * aqui só se formata o que já veio decidido.
 */

/** O texto do corpo, sem as etiquetas de HTML. */
const semHtml = (html) => String(html || '')
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim();

/** Quantas letras do corpo cabem no cartãozinho do ícone. */
export const LIMITE_DO_RESUMO = 120;

/**
 * As primeiras linhas do aviso, para dar contexto sem abrir o mural.
 *
 * Corta na PALAVRA, e não na letra: cortar no meio de uma palavra faz o
 * resumo parecer defeito, e a reticência já diz que há mais.
 */
export const resumoDoAviso = (aviso) => {
    const texto = semHtml(aviso?.body);

    if (texto === '') return '';
    if (texto.length <= LIMITE_DO_RESUMO) return texto;

    const cortado = texto.slice(0, LIMITE_DO_RESUMO);
    const ultimoEspaco = cortado.lastIndexOf(' ');

    return `${(ultimoEspaco > 40 ? cortado.slice(0, ultimoEspaco) : cortado).trim()}...`;
};

/**
 * Quanto tempo o aviso ainda tem, em palavras.
 *
 * Em HORAS enquanto for menos de um dia, e em DIAS daí para cima: "expira em
 * 2 dias" é o que a pessoa precisa saber de um recado que dura uma semana, e
 * "expira em 3 horas" é o que importa quando é para hoje.
 *
 * O prazo vencido não devolve texto negativo: o ícone não mostra aviso
 * vencido (o servidor já o tirou da lista), e se um chegar aqui por uma
 * resposta velha em cache, "expira em -2 horas" seria pior que nada.
 */
export const prazoLegivel = (iso, agora = new Date()) => {
    if (!iso) return '';

    const fim = new Date(iso);

    if (Number.isNaN(fim.getTime())) return '';

    const minutos = Math.round((fim.getTime() - agora.getTime()) / 60000);

    if (minutos <= 0) return 'Expirado';
    if (minutos < 60) return `Expira em ${minutos} min`;

    const horas = Math.round(minutos / 60);

    if (horas < 24) return `Expira em ${horas} ${horas === 1 ? 'hora' : 'horas'}`;

    const dias = Math.round(horas / 24);

    return `Expira em ${dias} ${dias === 1 ? 'dia' : 'dias'}`;
};

/**
 * A data e a hora do prazo, por extenso, para o comunicado aberto no mural.
 *
 * Pedido do dono do produto (20/10/2026): "mostrar no comunicado, quando
 * tiver, o horário e data de expiração".
 *
 * O rótulo fala de DESTAQUE, e não de validade do aviso: passada a data, o
 * aviso sai do ícone e continua ali, aberto na tela de quem está lendo.
 * Escrever "expira em" nesta tela diria, para quem lê o comunicado, que ele
 * vai sumir -- e não vai.
 *
 * Devolve `null` quando não há prazo: aviso comum não ganha linha nenhuma.
 */
export const prazoDoComunicado = (iso, agora = new Date()) => {
    if (!iso) return null;

    const fim = new Date(iso);

    if (Number.isNaN(fim.getTime())) return null;

    const quando = fim.toLocaleString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });

    return fim.getTime() <= agora.getTime()
        ? `Destaque encerrado em ${quando}`
        : `Em destaque até ${quando}`;
};
